"use client";

import { useEffect, useState } from "react";
import { z } from "zod";

/** Directions for the room the books are in, in the order the dev tuner offers them. */
export const THEME_IDS = ["sunlit", "classic", "gallery", "studio", "modern", "desk"] as const;

export type ThemeId = (typeof THEME_IDS)[number];

export const THEME_LABELS = {
  sunlit: "Sunlit oak",
  classic: "Classic case",
  gallery: "Gallery",
  studio: "Dark studio",
  modern: "Modern wood",
  desk: "Desk",
} satisfies Record<ThemeId, string>;

/** The timbers, for the classic case and the modern shelves. */
export const SKIN_IDS = ["honey", "walnut", "oak", "cherry", "maple", "ash", "lacquer", "photo"] as const;

export type SkinId = (typeof SKIN_IDS)[number];

export const SKIN_LABELS = {
  honey: "Honey oak",
  walnut: "Walnut",
  oak: "White oak",
  cherry: "Cherry",
  maple: "Maple",
  ash: "Ebonised ash",
  lacquer: "Green lacquer",
  photo: "Walnut (photo)",
} satisfies Record<SkinId, string>;

/** How the bookcase is finished and lit. */
export type ShelfLook = {
  theme: ThemeId;
  skin: SkinId;
  /** Colour temperature of the room light, in kelvin; the shelf strips are warmer. */
  kelvin: number;
  /** Multiplies every light in the case. */
  brightness: number;
};

export const DEFAULT_LOOK: ShelfLook = { theme: "sunlit", skin: "honey", kelvin: 5000, brightness: 1 };

/** Each room's own light: warm lamplight for the case, daylight for the gallery. */
export const THEME_KELVIN = {
  sunlit: 5000,
  classic: 4800,
  gallery: 6200,
  studio: 5000,
  modern: 5800,
  desk: 5600,
} satisfies Record<ThemeId, number>;

const STORAGE_KEY = "bookshelf-look-v2";

// Anything missing or no longer offered falls back to the default.
const SavedLook = z.object({
  theme: z.enum(THEME_IDS).catch(DEFAULT_LOOK.theme),
  skin: z.enum(SKIN_IDS).catch(DEFAULT_LOOK.skin),
  kelvin: z.number().catch(DEFAULT_LOOK.kelvin),
  brightness: z.number().catch(DEFAULT_LOOK.brightness),
});

/**
 * The bookcase's look. In development it can be changed from the tuner and
 * is remembered between reloads; elsewhere it is always the default.
 */
export function useShelfLook(): [ShelfLook, (look: ShelfLook) => void] {
  const [look, setLook] = useState(DEFAULT_LOOK);

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;
    try {
      const saved = SavedLook.safeParse(JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "null"));
      if (saved.success) setLook(saved.data);
    } catch {
      // A corrupt entry just means the default.
    }
  }, []);

  const update = (next: ShelfLook) => {
    setLook(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  };

  return [look, update];
}

/** The colour of a black body at `kelvin`, as a hex string (Tanner Helland's fit). */
export function kelvinToHex(kelvin: number): string {
  const t = Math.min(40000, Math.max(1000, kelvin)) / 100;
  const red = t <= 66 ? 255 : 329.698727446 * (t - 60) ** -0.1332047592;
  const green = t <= 66 ? 99.4708025861 * Math.log(t) - 161.1195681661 : 288.1221695283 * (t - 60) ** -0.0755148492;
  const blue = t >= 66 ? 255 : t <= 19 ? 0 : 138.5177312231 * Math.log(t - 10) - 305.0447927307;
  const channel = (value: number) =>
    Math.round(Math.min(255, Math.max(0, value)))
      .toString(16)
      .padStart(2, "0");
  return `#${channel(red)}${channel(green)}${channel(blue)}`;
}
